import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { QrCodeView } from '@/components/QrCodeView';
import { QrScannerSheet } from '@/components/QrScannerSheet';
import { container } from '@/services/container';
import { useIdentityStore } from '@/store/identity';
import { useWalletStore } from '@/store/wallet';
import { useUiStore } from '@/store/ui';
import { formatDpb, shortId } from '@/lib/format';
import { buildPayUri } from '@/lib/qrPay';
import { MeshLinkError } from '@/lib/result';

/** 余额展示：微单位 → DPB（保留 4 位小数，bigint 安全）。 */
const showDpb = (micro: string): string => formatDpb(micro, 4);

/** 钱包：余额 / 水龙头 / 转账 / 收款码 / 扫码付款 / 交易历史。 */
export function WalletPage(): JSX.Element {
  const balanceMicro = useWalletStore((s) => s.balanceMicro);
  const txs = useWalletStore((s) => s.txs);
  const faucetClaimed = useWalletStore((s) => s.faucetClaimed);
  const registered = useIdentityStore((s) => s.registered);
  const faucetClaimedMeta = useIdentityStore((s) => s.faucetClaimed);

  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  /** 收款码 / 扫码付款 弹窗 */
  const [payCodeOpen, setPayCodeOpen] = useState(false);
  const [scanPayOpen, setScanPayOpen] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payBusy, setPayBusy] = useState(false);

  const myAddr = container.identity.address ?? '';
  const payUri = buildPayUri(myAddr, payAmount);

  const refresh = async (): Promise<void> => {
    try {
      await container.wallet.getBalance();
      await container.wallet.history();
    } catch (err) {
      // getBalance / history 可能因 E_NOT_IMPLEMENTED 失败（链客户端未就绪或 MockChain 不支持某些方法）。
      // 此时 UI 应静默展示 balanceMicro='0'，不抛 Toast 以免干扰用户。
      if (err instanceof MeshLinkError && err.code === 'E_NOT_IMPLEMENTED') return;
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const claim = async (): Promise<void> => {
    setBusy(true);
    try {
      await container.wallet.claimFaucet();
      useUiStore.getState().pushToast('success', '新手奖励已到账');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  };

  const register = async (): Promise<void> => {
    setBusy(true);
    try {
      await container.wallet.registerDid();
      useUiStore.getState().pushToast('success', 'DID 已注册');
    } catch (err) {
      useUiStore.getState().pushError(err instanceof MeshLinkError ? err.code : 'E_UNKNOWN');
    } finally {
      setBusy(false);
    }
  };

  const transfer = async (): Promise<void> => {
    if (!to.trim() || !amount.trim()) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return;
    }
    setBusy(true);
    try {
      await container.wallet.transfer(to, amount);
      setTo('');
      setAmount('');
      useUiStore.getState().pushToast('success', '转账已提交');
      await refresh();
    } catch (err) {
      const code = err instanceof MeshLinkError ? err.code : 'E_UNKNOWN';
      // 仅对 Nonce 冲突注入重试处理器（catalog action='retry'）；其它码保留内置路由（如 topup/switchRelay），
      // 避免 onAction 越过内置路由造成「按钮文案与行为不一致」。
      useUiStore.getState().pushError(
        code,
        code === 'E_NONCE_CONFLICT' ? { onAction: () => void transfer() } : undefined,
      );
    } finally {
      setBusy(false);
    }
  };

  /** 扫码收款码 → 付款：复用 wallet.transfer 签名上链。返回是否成功（成功后关闭弹窗）。 */
  const scanPay = async (toAddr: string, amountDpb: string): Promise<boolean> => {
    if (!toAddr || !amountDpb) {
      useUiStore.getState().pushError('E_INVALID_INPUT');
      return false;
    }
    setPayBusy(true);
    try {
      await container.wallet.transfer(toAddr, amountDpb);
      useUiStore.getState().pushToast('success', `已向 ${shortId(toAddr, 8, 6)} 支付 ${amountDpb} DPB`);
      setScanPayOpen(false);
      await refresh();
      return true;
    } catch (err) {
      const code = err instanceof MeshLinkError ? err.code : 'E_UNKNOWN';
      useUiStore.getState().pushError(code);
      return false;
    } finally {
      setPayBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <section className="card p-5">
        <h2 className="text-sm font-medium text-text">余额</h2>
        <p className="mt-1 text-2xl font-semibold text-text">{showDpb(balanceMicro)} DPB</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Badge tone={registered ? 'ok' : 'warn'}>{registered ? 'DID 已注册' : 'DID 未注册'}</Badge>
          <Badge tone={faucetClaimed || faucetClaimedMeta ? 'gold' : 'neutral'}>
            {faucetClaimed || faucetClaimedMeta ? '已领新手奖励' : '未领新手奖励'}
          </Badge>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {registered ? null : (
            <Button variant="outline" size="sm" loading={busy} onClick={() => void register()}>
              注册 DID
            </Button>
          )}
          {faucetClaimed || faucetClaimedMeta ? null : (
            <Button variant="outline" size="sm" loading={busy} onClick={() => void claim()}>
              领取新手奖励
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setPayCodeOpen(true)}>
            收款码
          </Button>
          <Button variant="outline" size="sm" onClick={() => setScanPayOpen(true)}>
            扫码付款
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void refresh()}>
            刷新
          </Button>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">转账</h2>
        <div className="space-y-3">
          <Input
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="收款地址 0x…"
          />
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="金额（DPB），如 1.5"
          />
          <Button size="sm" loading={busy} onClick={() => void transfer()}>
            转账
          </Button>
        </div>
      </section>

      <section className="card p-5">
        <h2 className="mb-3 text-sm font-medium text-text">交易记录</h2>
        {txs.length === 0 ? (
          <EmptyState title="暂无交易" description="领取新手奖励后即可看到记录" />
        ) : (
          <ul className="space-y-2">
            {txs.map((t) => (
              <li
                key={t.txHash}
                className="flex items-center justify-between rounded-btn bg-panel-2 px-3 py-2 text-xs"
              >
                <span className="text-text">{t.type}</span>
                <span className="text-dim">
                  {t.amountMicro ? `${showDpb(t.amountMicro)} DPB` : '—'}
                </span>
                <span className="mono text-dim">#{t.blockHeight}</span>
                <span className="text-dim">{new Date(t.timestamp).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 收款码：输入金额（可选）生成 DPB 收款二维码 */}
      <Modal
        open={payCodeOpen}
        onOpenChange={setPayCodeOpen}
        title="DPB 收款码"
        description="让对方用「扫一扫」扫此码向你付款（不填金额 = 任意金额）"
      >
        <div className="flex flex-col items-center gap-3">
          {myAddr ? (
            <>
              <Input
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder="收款金额（DPB），留空表示任意金额"
                inputMode="decimal"
              />
              <QrCodeView value={payUri} size={208} copyable={false} label="扫一扫向我付款" />
              <p className="mono break-all text-[11px] text-dim">{myAddr}</p>
              <p className="text-center text-xs text-dim">
                {payAmount ? `将收取 ${payAmount} DPB` : '任意金额 · 对方输入后确认付款'}
              </p>
            </>
          ) : (
            <p className="text-center text-sm text-dim">未获得链地址，请先解锁钱包</p>
          )}
        </div>
      </Modal>

      {/* 扫码付款：识别 meshlink://pay 收款码 → 付款确认 → 转账 */}
      <Modal
        open={scanPayOpen}
        onOpenChange={setScanPayOpen}
        title="扫码付款"
        description="扫描对方的 DPB 收款码，确认后签名付款"
      >
        <QrScannerSheet myPeerId={myAddr || ''} onPay={scanPay} payBusy={payBusy} />
      </Modal>
    </div>
  );
}

export default WalletPage;
